/**
 * Universal Redirect Service
 *
 * Handles all redirect scenarios with scalable intent preservation:
 * - Free trial → pay
 * - No account → signup → pay
 * - Existing account → login → pay
 * - Magic links (future-ready)
 *
 * Integrates with existing Auth0 flow and sessionStorage system.
 */

/// <reference lib="dom" />

import { SETTINGS_TAB_CONFIG, type SettingsTabKey } from '../settings';

export interface RedirectIntent {
  /** Primary destination after auth/action completion */
  destination: string;

  /** Tab within destination (e.g., 'billing' for /settings?tab=billing) */
  tab?: string;

  /** Source of the redirect (for analytics) */
  source?: 'sms' | 'email' | 'web' | 'magic-link' | 'referral';

  /** Intent type for behavior customization */
  intent?: 'upgrade' | 'signup' | 'billing' | 'calendar' | 'chat' | 'verify';

  /** Magic link token (future feature) */
  magicToken?: string;

  /** Campaign tracking */
  campaign?: string;

  /** Additional parameters to preserve */
  params?: Record<string, string>;

  /** Expires at timestamp (for magic links) */
  expiresAt?: number;
}

export interface RedirectUrl {
  /** Clean semantic URL */
  url: string;

  /** Shortened URL for SMS */
  shortUrl?: string;
}

/**
 * Intent configuration - single source of truth
 * Maps intent types to their destinations and required tabs
 * Note: Auth requirements are handled by the router
 * Tab values come directly from SETTINGS_TAB_CONFIG for DRY principle
 */
interface IntentConfig {
  destination: string;
  tab?: SettingsTabKey; // Typed from settings component for extensibility
}

const INTENT_CONFIG: Record<string, IntentConfig> = {
  upgrade: {
    destination: '/settings',
    tab: SETTINGS_TAB_CONFIG.billing.key,
  },
  billing: {
    destination: '/settings',
    tab: SETTINGS_TAB_CONFIG.billing.key,
  },
  signup: {
    destination: '/settings',
    tab: SETTINGS_TAB_CONFIG.billing.key,
  },
  verify: {
    destination: '/settings',
    tab: SETTINGS_TAB_CONFIG.profile.key,
  },
  calendar: {
    destination: '/calendar-flow',
  },
  chat: {
    destination: '/chat',
  },
};

/**
 * Semantic route mapping - URL path to intent type
 */
const SEMANTIC_ROUTES: Record<string, string> = {
  '/upgrade': 'upgrade',
  '/pay': 'billing',
  '/join': 'signup',
  '/verify': 'verify',
};

class RedirectService {
  private readonly STORAGE_KEY = 'keysha_redirect_intent';
  private readonly FALLBACK_DESTINATION = '/dashboard';

  /**
   * Generate redirect URL for external links (SMS, email, etc.)
   */
  generateRedirectUrl(intent: Omit<RedirectIntent, 'expiresAt'>): RedirectUrl {
    // Get semantic route from intent type
    const semanticPath =
      Object.entries(SEMANTIC_ROUTES).find(
        ([_, intentType]) => intentType === intent.intent
      )?.[0] || '/r';

    const url = new window.URL(semanticPath, window.location.origin);

    // Add parameters
    if (intent.source) url.searchParams.set('source', intent.source);
    if (intent.campaign) url.searchParams.set('campaign', intent.campaign);
    if (intent.magicToken) url.searchParams.set('token', intent.magicToken);
    if (intent.tab) url.searchParams.set('tab', intent.tab);

    // Add custom params
    Object.entries(intent.params || {}).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return {
      url: url.toString(),
      // TODO: Integrate with URL shortener service for SMS
      shortUrl: url.toString(),
    };
  }

  /**
   * Parse redirect parameters from current URL
   */
  parseRedirectIntent(): RedirectIntent | null {
    const url = new window.URL(window.location.href);
    const params = url.searchParams;

    // Get intent from path or query param
    const intent =
      this.getIntentFromPath(url.pathname) ||
      (params.get('intent') as RedirectIntent['intent']);

    if (!intent && !params.has('source')) {
      return null;
    }

    // Get destination and tab from config (DRY - single source of truth)
    const config = intent ? INTENT_CONFIG[intent] : undefined;
    const destination =
      params.get('dest') || config?.destination || this.FALLBACK_DESTINATION;
    const tab = params.get('tab') || config?.tab;

    const redirectIntent: RedirectIntent = {
      destination,
      intent,
      source: (params.get('source') as RedirectIntent['source']) || undefined,
      campaign: params.get('campaign') || undefined,
      magicToken: params.get('token') || undefined,
      tab,
      params: this.extractCustomParams(params),
    };

    return redirectIntent;
  }

  /**
   * Store redirect intent for post-auth navigation
   */
  storeRedirectIntent(intent: RedirectIntent): void {
    try {
      // Set expiration if not provided (24 hours default)
      const intentWithExpiry: RedirectIntent = {
        ...intent,
        expiresAt: intent.expiresAt || Date.now() + 24 * 60 * 60 * 1000,
      };

      sessionStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify(intentWithExpiry)
      );

      // Also set auth_final_destination for compatibility with existing auth flow
      const finalUrl = this.buildFinalUrl(intentWithExpiry);
      sessionStorage.setItem('auth_final_destination', finalUrl);
    } catch (error) {
      console.error('Failed to store redirect intent:', error);
    }
  }

  /**
   * Retrieve and clear stored redirect intent
   */
  getStoredRedirectIntent(): RedirectIntent | null {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);

      if (!stored) return null;

      const intent: RedirectIntent = JSON.parse(stored);

      // Check expiration
      if (intent.expiresAt && Date.now() > intent.expiresAt) {
        this.clearRedirectIntent();
        return null;
      }

      return intent;
    } catch (error) {
      console.error('Failed to retrieve redirect intent:', error);
      return null;
    }
  }

  /**
   * Clear stored redirect intent
   */
  clearRedirectIntent(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Execute redirect after successful auth/action
   */
  executeRedirect(navigate: (path: string) => void): boolean {
    const intent = this.getStoredRedirectIntent();

    if (!intent) return false;

    const finalUrl = this.buildFinalUrl(intent);

    this.clearRedirectIntent();

    navigate(finalUrl);
    return true;
  }

  /**
   * Check if user needs authentication for intended destination
   * Note: Router config is the source of truth for auth requirements
   */
  requiresAuth(intent: RedirectIntent): boolean {
    // Assume most app destinations require auth except landing pages
    const publicDestinations = ['/', '/blog', '/policies'];
    return !publicDestinations.includes(intent.destination);
  }

  // Private helper methods

  private getIntentFromPath(
    pathname: string
  ): RedirectIntent['intent'] | undefined {
    return SEMANTIC_ROUTES[pathname] as RedirectIntent['intent'] | undefined;
  }

  private extractCustomParams(params: URLSearchParams): Record<string, string> {
    const reserved = ['source', 'intent', 'campaign', 'token', 'tab', 'dest'];
    const custom: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reserved.includes(key)) {
        custom[key] = value;
      }
    });

    return custom;
  }

  private buildFinalUrl(intent: RedirectIntent): string {
    const url = new window.URL(intent.destination, window.location.origin);

    // Add tab parameter for settings page
    if (intent.destination === '/settings' && intent.tab) {
      url.searchParams.set('tab', intent.tab);
    }

    // Preserve any additional params
    Object.entries(intent.params || {}).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    const finalUrl = url.pathname + url.search;
    return finalUrl;
  }
}

// Export singleton instance
export const redirectService = new RedirectService();

// Convenience functions for common use cases

/**
 * Generate upgrade link for SMS/email campaigns
 */
export function generateUpgradeLink(
  source: 'sms' | 'email',
  campaign?: string
): string {
  return redirectService.generateRedirectUrl({
    destination: '/settings',
    tab: 'billing',
    intent: 'upgrade',
    source,
    campaign,
  }).url;
}

/**
 * Generate signup link with post-signup redirect to billing
 */
export function generateSignupToBillingLink(
  source: 'sms' | 'email',
  campaign?: string
): string {
  return redirectService.generateRedirectUrl({
    destination: '/settings',
    tab: 'billing',
    intent: 'signup',
    source,
    campaign,
  }).url;
}

/**
 * Generate magic login link (future feature)
 */
export function generateMagicLoginLink(
  email: string,
  destination: string = '/dashboard',
  magicToken: string
): string {
  return redirectService.generateRedirectUrl({
    destination,
    intent: 'upgrade',
    source: 'magic-link',
    magicToken,
    params: { email },
  }).url;
}
